class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (!this.classList.contains('active')) return;

      const inner = event.target.closest('.drawer__inner');
      const clickedOverlay = event.target.closest('#CartDrawer-Overlay, .cart-drawer__overlay, .mil-cart__overlay');

      if (clickedOverlay || !inner) {
        event.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartSelectors = [
      '#cart-icon-bubble',
      '.mil-header__cart',
      '[data-mil-cart-drawer-trigger]'
    ];
    const cartLinks = document.querySelectorAll(cartSelectors.join(','));
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  lockScroll() {
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--mil-cart-scrollbar-width', `${scrollbarWidth}px`);
    document.documentElement.classList.add('mil-cart-drawer-lock');
    document.body.classList.add('overflow-hidden', 'mil-cart-drawer-lock');
  }

  unlockScroll() {
    document.documentElement.classList.remove('mil-cart-drawer-lock');
    document.body.classList.remove('overflow-hidden', 'mil-cart-drawer-lock');
    document.documentElement.style.removeProperty('--mil-cart-scrollbar-width');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.setHeaderCartIconAccessibility();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // When quantity reaches 0 (Remove button, minus button, or typing 0) Shopify's
  // standard change-flow aborts on the "already sold out" warning and never
  // re-renders, so sold-out items get stuck in the cart. Force a reliable removal.
  updateQuantity(line, quantity, event, name, variantId) {
    if (Number(quantity) === 0) {
      this.milRemoveLine(line);
      return;
    }
    super.updateQuantity(line, quantity, event, name, variantId);
  }

  milRemoveLine(line) {
    if (typeof this.enableLoading === 'function') this.enableLoading(line);

    const removeBtn = document.getElementById(`CartDrawer-Remove-${line}`);
    const lineKey = removeBtn ? removeBtn.dataset.lineKey : null;
    const sections = this.getSectionsToRender().map((section) => section.section);
    const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    const base = { quantity: 0, sections, sections_url: window.location.pathname };

    const renderState = (state) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (state && state.sections && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.toggle('is-empty', state.item_count === 0);
        cartDrawer.renderContents(state);
      } else {
        window.location.href = cartUrl;
      }
    };

    // Žiedo dydžio pora: jei ši eilutė (arba jos porininkas) turi bendrą žymenį,
    // ištrinam VISAS tos grupės eilutes vienu kartu (žiedą + paslaugą).
    const itemEl = document.getElementById(`CartDrawer-Item-${line}`);
    const group = itemEl ? itemEl.getAttribute('data-mil-resize-group') : null;
    if (group) {
      const esc = (window.CSS && CSS.escape) ? CSS.escape(group) : group.replace(/["\\]/g, '\\$&');
      const groupKeys = [];
      document.querySelectorAll(`[data-mil-resize-group="${esc}"]`).forEach((el) => {
        const rb = el.querySelector('cart-remove-button');
        const k = rb ? rb.dataset.lineKey : null;
        if (k && groupKeys.indexOf(k) === -1) groupKeys.push(k);
      });
      if (lineKey && groupKeys.indexOf(lineKey) === -1) groupKeys.push(lineKey);

      if (groupKeys.length > 1) {
        const updates = {};
        groupKeys.forEach((k) => { updates[k] = 0; });
        fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
        })
          .then((response) => response.json())
          .catch(() => null)
          .then(renderState)
          .finally(() => {
            if (typeof this.disableLoading === 'function') this.disableLoading(line);
          });
        return;
      }
    }

    const change = (payload) =>
      fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, base, payload)),
      })
        .then((response) => response.json())
        .catch(() => null);

    // Prefer removing by line key (works on out-of-stock lines); fall back to line index.
    const firstAttempt = lineKey ? change({ id: lineKey }) : change({ line: Number(line) });

    firstAttempt
      .then((state) => {
        const failed = !state || state.status || state.errors || !state.sections;
        // If the key-based attempt reported an issue, retry by line index.
        if (failed && lineKey) return change({ line: Number(line) });
        return state;
      })
      .then(renderState)
      .finally(() => {
        if (typeof this.disableLoading === 'function') this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(function () {
  function setButtonState(button, isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle('is-loading', isLoading);
  }

  function showCartError(message) {
    const target = document.getElementById('CartDrawer-CartErrors');
    if (!target) return;
    // Tuščia/nenurodyta žinutė = klaidos laukelį išvalom.
    target.textContent = message ? message : '';
  }

  function clearCartError() {
    showCartError('');
  }

  async function addVariantToCart(button) {
    const variantId = button && button.getAttribute('data-mil-cart-add-variant');
    if (!variantId) return;

    setButtonState(button, true);
    clearCartError();

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sections,
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      const parsed = await response.json();
      if (!response.ok || parsed.status) {
        throw new Error(parsed.description || parsed.message || window.cartStrings?.error || 'Cart error');
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsed);
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      showCartError(error.message || window.cartStrings?.error || 'Cart error');
      setButtonState(button, false);
    }
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-mil-cart-add-variant]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.classList.contains('is-loading')) return;
    addVariantToCart(button);
  });
})();

(function () {
  document.addEventListener(
    'click',
    function (event) {
      const minusButton = event.target.closest(
        'cart-drawer-items quantity-input button[name="minus"]'
      );
      if (!minusButton) return;

      const quantityWrapper = minusButton.closest('quantity-input');
      const quantityInput = quantityWrapper && quantityWrapper.querySelector('input.quantity__input');
      if (!quantityInput) return;

      const currentValue = parseInt(quantityInput.value, 10);
      // Perimame tik esant paskutiniam vienetui; 2+ palieka tvarkyti komponentui.
      if (!Number.isFinite(currentValue) || currentValue > 1) return;

      // Neleidžiam kiekio komponentui apdoroti šio paspaudimo.
      event.preventDefault();
      event.stopImmediatePropagation();

      const cartItems = minusButton.closest('cart-drawer-items');
      const line = quantityInput.dataset.index;
      if (cartItems && typeof cartItems.milRemoveLine === 'function' && line) {
        cartItems.milRemoveLine(line);
      }
    },
    true // capture faze – suveikia anksčiau už paties komponento klausytoją
  );
})();